/**
 * Side navigation
 * 
 * Per design.md: Use semantic tokens, lucide icons only
 * Mobile responsive: Hide on small screens, show hamburger menu
 */

import { NavLink } from 'react-router-dom';
import { useState } from 'react';
import {
    LayoutDashboard,
    FileText,
    BarChart2,
    Users,
    CreditCard,
    Settings,
    Sparkles,
    Menu,
    X,
    User,
    History,
    FolderOpen,
    MessageSquareText,
    Phone,
} from '@uprosal/ui';
import { cn, Button } from '@uprosal/ui';

interface NavItem {
    to: string;
    icon: React.ComponentType<{ className?: string }>;
    label: string;
}

const NAV_ITEMS: NavItem[] = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/templates', icon: FileText, label: 'Templates' },
    { to: '/proof', icon: FolderOpen, label: 'Proof Library' },
    { to: '/qa-library', icon: MessageSquareText, label: 'Q&A Library' },
    { to: '/calls', icon: Phone, label: 'Calls' },
    { to: '/history', icon: History, label: 'History' },
    { to: '/profiles', icon: User, label: 'Profiles' },
    { to: '/analytics', icon: BarChart2, label: 'Analytics' },
    { to: '/team', icon: Users, label: 'Team' },
    { to: '/billing', icon: CreditCard, label: 'Billing' },
    { to: '/settings', icon: Settings, label: 'Settings' },
];

function SidebarContent({ onNavClick }: { onNavClick?: () => void }) {
    return (
        <>
            {/* Logo */}
            <div className="h-16 border-b border-border flex items-center px-6">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <span className="text-xl font-bold text-sidebar-foreground">Uprosal</span>
                </div>
            </div>

            {/* Navigation Links */}
            <nav className="flex-1 px-3 py-4 space-y-1">
                {NAV_ITEMS.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={onNavClick}
                        className={({ isActive }) =>
                            cn(
                                'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150',
                                isActive
                                    ? 'bg-sidebar-muted text-primary border-l-2 border-primary'
                                    : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-muted/50'
                            )
                        }
                    >
                        <item.icon className="w-4 h-4 shrink-0" />
                        {item.label}
                    </NavLink>
                ))}
            </nav>

            {/* Bottom section removed - AI Tokens widget was redundant */}
        </>
    );
}

export function SideNav() {
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <>
            {/* Mobile hamburger button - only visible on small screens */}
            <Button
                variant="ghost"
                size="icon"
                className="fixed top-4 left-4 z-50 md:hidden"
                onClick={() => setMobileOpen(true)}
            >
                <Menu className="w-6 h-6 text-foreground" />
            </Button>

            {/* Mobile Overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Mobile Sidebar (Sheet) */}
            <aside
                className={cn(
                    'fixed left-0 top-0 h-screen w-64 bg-sidebar z-50 flex flex-col transition-transform duration-300 md:hidden',
                    mobileOpen ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-4 right-4"
                    onClick={() => setMobileOpen(false)}
                >
                    <X className="w-5 h-5" />
                </Button>
                <SidebarContent onNavClick={() => setMobileOpen(false)} />
            </aside>

            {/* Desktop Sidebar - hidden on mobile */}
            <aside className="hidden md:flex w-56 border-r border-border bg-sidebar h-screen sticky top-0 flex-col">
                <SidebarContent />
            </aside>
        </>
    );
}
